<template>
  <v-card
    v-if="surrogated_student_info"
    class="student-home mx-auto"
    color="#fffdfb"
  >
    <v-card flat tile color="#fff3ea" class="profile-panel pa-4 pa-sm-6">
      <v-row align="center" no-gutters>
        <v-col cols="auto" class="d-flex">
          <v-responsive
            :aspect-ratio="10 / 13"
            width="130"
            class="student-photo"
          >
            <img
              v-if="photo_url"
              :src="photo_url"
              :alt="display_name"
              class="student-photo-image"
            />
            <v-card
              v-else
              flat
              tile
              color="deep-orange lighten-5"
              class="d-flex align-center justify-center fill-height"
              width="100%"
            >
              <v-icon color="deep-orange lighten-2" size="72">
                mdi-account-school-outline
              </v-icon>
            </v-card>
          </v-responsive>
        </v-col>
        <v-col class="profile-name ml-3 ml-sm-6 pa-0">
          <h1 class="student-name font-weight-bold brown--text text--darken-2">
            {{ display_name }}
          </h1>
          <div v-if="secondary_name" class="text-body-1 brown--text text--lighten-1 mt-1">
            {{ secondary_name }}
          </div>
        </v-col>
      </v-row>
    </v-card>

    <v-divider class="peach-divider" />

    <v-card-text class="pa-4 pa-sm-6">
      <v-alert
        v-if="error_message"
        class="mb-6"
        color="deep-orange"
        icon="mdi-alert-circle-outline"
        outlined
        text
      >
        {{ error_message }}
        <v-btn class="ml-2" color="deep-orange" small text @click="fetch_dashboard_data">
          {{ $t("home.try_again") }}
        </v-btn>
      </v-alert>

      <v-card outlined color="white" class="academic-panel">
        <v-card-title class="pl-6">
          <div class="font-weight-bold brown--text text--darken-2">
            {{ $t("home.academic_information") }}
          </div>
        </v-card-title>

        <v-divider class="peach-divider" />

        <v-card-text class="pa-4">
          <v-row v-if="academic_items.length" dense>
            <v-col
              v-for="item in academic_items"
              :key="item.key"
              cols="12"
              sm="6"
              lg="4"
              class="d-flex"
            >
              <v-list-item class="academic-item flex-grow-1 px-2 py-0">
                <v-list-item-avatar
                  :class="[item.avatar_color || 'brown lighten-5', 'mr-2']"
                  size="32"
                >
                  <v-icon :color="item.icon_color || 'brown lighten-1'" size="18">
                    {{ item.icon }}
                  </v-icon>
                </v-list-item-avatar>
                <v-list-item-content class="py-1">
                  <v-list-item-subtitle class="academic-label brown--text text--lighten-1">
                    {{ item.label }}
                  </v-list-item-subtitle>
                  <v-list-item-title class="academic-value text-body-1 brown--text text--darken-2">
                    {{ item.value }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-col>
          </v-row>

        </v-card-text>
      </v-card>
    </v-card-text>
  </v-card>

      <v-row
        v-else-if="is_authorized_officer"
        align="center"
        justify="center"
        class="ma-0 officer-shell"
      >
        <v-col cols="12">
          <v-card class="officer-card mx-auto" width="400" max-width="100%">
            <v-toolbar-title class="officer-title d-flex align-center pa-4">
              <v-avatar color="deep-orange lighten-5" size="44" class="mr-3">
                <v-icon color="deep-orange darken-1">mdi-account-switch-outline</v-icon>
              </v-avatar>
              <div>
                <div class="font-weight-bold">{{ $t("home.surrogate_title") }}</div>
              </div>
            </v-toolbar-title>
            <v-form ref="form" v-model="valid_surrogated" @submit.prevent="loginJWTSurrogatedStudent">
              <v-card-text>
                <v-text-field
                  v-model="input_surrogated_student_id"
                  :label="$t('home.student_id')"
                  :rules="surrogetedStudentIdRules"
                  counter="8"
                  maxlength="8"
                  outlined
                  required
                />
              </v-card-text>
              <v-card-actions class="px-4 pb-5 pt-0">
                <v-spacer />
                <v-btn
                  :color="can_surrogate_login ? 'deep-orange lighten-1' : undefined"
                  :dark="can_surrogate_login"
                  depressed
                  type="submit"
                  :disabled="!can_surrogate_login"
                >
                  <v-icon left>mdi-login-variant</v-icon>
                  {{ $t("home.surrogate_login") }}
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-col>
      </v-row>
</template>

<script>
import UserProvider from "@/services/user.provider";

const userService = new UserProvider();

export default {
  name: "Home",

  data: () => ({
    is_authen: false,
    is_officer: false,
    is_authorized_officer: false,
    user_info: null,
    surrogated_student_id: null,
    surrogated_student_info: null,
    student_data: {},
    photo_url: null,
    loading: false,
    error_message: "",
    valid_surrogated: false,
    input_surrogated_student_id: "",
  }),

  computed: {
    is_english() {
      return this.$i18n.locale === "en";
    },

    student_id() {
      return this.$store.state.user.surrogated_student_id || null;
    },

    can_surrogate_login() {
      return this.valid_surrogated && /^\d{8}$/.test(this.input_surrogated_student_id);
    },

    is_international_student() {
      return String(this.student_data.inter_student) === "1";
    },

    display_name() {
      const language = this.is_international_student ? "en" : "th";
      const apiName = this.full_name(language);
      if (apiName) return apiName;

      const info = this.surrogated_student_info || {};
      const fallback = language === "en"
        ? [info.fname_en, info.lname_en]
        : [info.fname_th, info.lname_th];
      return fallback.filter(Boolean).join(" ") || this.$t("home.student");
    },

    secondary_name() {
      if (this.is_international_student) return "";

      const name = this.full_name("en");
      if (name) return name !== this.display_name ? name : "";

      const info = this.surrogated_student_info || {};
      const fallback = [info.fname_en, info.lname_en].filter(Boolean).join(" ");
      if (fallback) return fallback !== this.display_name ? fallback : "";

      return "";
    },

    is_graduate() {
      return Number(this.student_data.level_id) > 1;
    },

    student_type() {
      if (String(this.student_data.inter_student) === "0") {
        return this.$t("home.thai_student");
      }
      if (String(this.student_data.inter_student) === "1") {
        return this.$t("home.international_student");
      }
      return "";
    },

    student_status() {
      const status = String(this.student_data.status || "").trim();
      const studying_statuses = ["ผ่าน", "เรียน"];
      const graduated_statuses = ["จบ", "สำเร็จการศึกษา"];

      if (studying_statuses.includes(status)) {
        return this.$t("home.currently_studying");
      }

      if (graduated_statuses.includes(status)) {
        const council_approved = String(this.student_data.approve_council) === "1"
          ? ` (${this.$t("home.council_approved")})`
          : "";
        return `${this.$t("home.graduated")}${council_approved}`;
      }

      return status;
    },

    academic_items() {
      const admission = [this.student_data.admis_term, this.student_data.admis_year]
        .filter((value) => value !== null && value !== undefined && value !== "")
        .join(" / ");
      const curriculum = this.localized("curr_tname", "curr_ename");
      const branch = this.localized("curr2_tname", "curr2_ename");

      const items = [
        {
          key: "student_id",
          label: this.$t("home.student_id"),
          value: this.student_id,
          icon: "mdi-card-account-details-outline",
          avatar_color: "deep-orange lighten-5",
          icon_color: "deep-orange darken-3",
        },
        {
          key: "level",
          label: this.$t("home.level"),
          value: this.localized("level_name_th", "level_name_en"),
          icon: "mdi-school-outline",
          avatar_color: "blue lighten-5",
          icon_color: "blue darken-3",
        },
        {
          key: "faculty",
          label: this.localized("prefix_name_th", "prefix_name_en") || this.$t("home.faculty"),
          value: this.localized("faculty_name", "faculty_ename"),
          icon: "mdi-domain",
        },
        {
          key: "department",
          label: this.$t("home.department"),
          value: this.localized("dept_name", "dept_ename"),
          icon: "mdi-source-branch",
        },
        {
          key: "branch",
          label: this.$t("home.branch"),
          value: branch,
          icon: "mdi-sitemap-outline",
        },
        {
          key: "curriculum",
          label: this.$t("home.curriculum"),
          value: curriculum,
          icon: "mdi-book-open-page-variant-outline",
        },
        {
          key: "student_type",
          label: this.$t("home.student_type"),
          value: this.student_type,
          icon: "mdi-earth",
        },
        {
          key: "admission",
          label: this.$t("home.admission_term"),
          value: admission,
          icon: "mdi-calendar-import",
        },
      ];

      if (this.is_graduate) {
        items.push(
          {
            key: "admission_type",
            label: this.$t("home.admission_type"),
            value: this.localized("study_admit_type_name_th", "study_admit_type_name_en"),
            icon: "mdi-account-arrow-left-outline",
          },
          {
            key: "study_plan",
            label: this.$t("home.study_plan"),
            value: this.localized("study_plan_name_th", "study_plan_name_en"),
            icon: "mdi-map-marker-path",
          },
          {
            key: "academic_advisor",
            label: this.$t("home.academic_advisor"),
            value: this.localized("academic_advisor_name_th", "academic_advisor_name_en"),
            icon: "mdi-account-tie-outline",
          },
          {
            key: "thesis_advisor",
            label: this.$t("home.thesis_advisor"),
            value: this.localized("thesis_advisor_name_th", "thesis_advisor_name_en"),
            icon: "mdi-account-tie",
          });
      }

      items.push({
        key: "status",
        label: this.$t("home.student_status"),
        value: this.student_status,
        icon: "mdi-account-check-outline",
      });

      return items.map((item) => ({
        ...item,
        value: item.value === null || item.value === undefined || item.value === ""
          ? "-"
          : item.value,
      }));
    },

    surrogetedStudentIdRules() {
      return [
        (value) => !!value || this.$t("home.student_id_required"),
        (value) => /^\d{8}$/.test(value || "") || this.$t("home.student_id_length"),
      ];
    },
  },

  methods: {
    localized(thaiKey, englishKey) {
      const preferred = this.is_english ? englishKey : thaiKey;
      const fallback = this.is_english ? thaiKey : englishKey;
      return this.student_data[preferred] || this.student_data[fallback] || "";
    },

    full_name(language) {
      const suffix = language === "en" ? "en" : "th";
      return [
        this.student_data[`prename_${suffix}`],
        this.student_data[`name_${suffix}`],
        this.student_data[`surname_${suffix}`],
      ]
        .filter(Boolean)
        .join(" ")
        .trim();
    },

    update() {
      const user = this.$store.state.user;
      if (!user.is_authen) return;

      this.is_authen = user.is_authen;
      this.is_officer = user.is_officer;
      this.is_authorized_officer = user.is_authorized_officer;
      this.user_info = user.user_info;
      this.surrogated_student_id = user.surrogated_student_id;
      this.surrogated_student_info = user.surrogated_student_info;
    },

    async fetch_student_photo() {
      if (this.photo_url) {
        window.URL.revokeObjectURL(this.photo_url);
        this.photo_url = null;
      }

      try {
        const photo = await userService.getStudentCardPhoto(this.student_id);
        if (photo && photo.size) {
          this.photo_url = window.URL.createObjectURL(photo);
        }
      } catch (error) {
        if (this.photo_url) window.URL.revokeObjectURL(this.photo_url);
        this.photo_url = null;
      }
    },

    async fetch_dashboard_data() {
      if (!this.student_id || String(this.student_id).length !== 8) return;

      this.loading = true;
      this.error_message = "";

      const studentRequest = userService
        .getStudentData(this.student_id)
        .then((data) => {
          this.student_data = data || {};
        })
        .catch(() => {
          this.student_data = {};
          this.error_message = this.$t("home.load_error");
        });

      await Promise.all([studentRequest, this.fetch_student_photo()]);
      this.loading = false;
    },

    async loginJWTSurrogatedStudent() {
      if (!this.$refs.form || !this.$refs.form.validate()) return;

      sessionStorage.setItem("surrogated_student_id", this.input_surrogated_student_id);
      try {
        await this.$store.dispatch("user/getUserInfo");
        this.update();
        await this.fetch_dashboard_data();
      } catch (error) {
        const message =
          error && error.response && error.response.data
            ? error.response.data.message
            : this.$t("home.load_error");
        this.$toasted.error(JSON.stringify(message), { duration: 0 });
      }
    },
  },

  async mounted() {
    this.update();
    if (this.surrogated_student_info) await this.fetch_dashboard_data();
  },

  beforeDestroy() {
    if (this.photo_url) window.URL.revokeObjectURL(this.photo_url);
  },
};
</script>

<style scoped>
.student-home {
  max-width: 1440px;
  overflow: hidden;
}

.peach-divider {
  border-color: rgba(224, 182, 146, 0.42) !important;
}

.student-photo {
  overflow: hidden;
  border: 3px solid rgba(255, 255, 255, 0.92);
  border-radius: 16px !important;
  box-shadow: 0 12px 28px rgba(133, 91, 66, 0.18);
}

.student-photo-image {
  display: block;
  width: 100%;
  height: 100%;
}

.profile-name {
  min-width: 0;
}

.student-name {
  font-size: 30px !important;
}

.academic-panel,
.officer-card {
  overflow: hidden;
  border: 1px solid #f1e1d6;
}

.academic-panel {
  border-radius: 12px;
  border-color: rgba(224, 182, 146, 0.42) !important;
  box-shadow: 0 16px 30px rgba(171, 126, 97, 0.08) !important;
}

.academic-item {
  width: 100%;
  min-height: 54px;
  border-radius: 10px;
  background: #fffaf4;
}

.academic-value {
  overflow: visible;
  white-space: normal;
  line-height: 1.4;
  word-break: break-word;
}

.academic-label {
  overflow: visible;
  font-size: 12px !important;
}

.officer-shell {
  min-height: 65vh;
  padding: 24px;
  background: #fffdfb;
}

.officer-card {
  background: #fffaf6;
}

</style>
